/**
 * Service for managing table assignment persistence in localStorage
 * Handles all storage operations for table mappings and MRT table arrays
 */
class TableAssignmentStorage {
  constructor() {
    this.storageKeyPrefix = 'tableAssignments_'
    this.templateTablesKeyPrefix = 'templateTables_'
    this.replacementTablesKeyPrefix = 'replacementTables_'
  }

  /**
   * Generate storage key for a specific document
   * @param {string} documentId - The document ID
   * @returns {string} The localStorage key
   */
  getStorageKey(documentId) {
    if (!documentId) {
      console.warn('TableAssignmentStorage: No documentId provided')
      return null
    }
    return `${this.storageKeyPrefix}${documentId}`
  }

  /**
   * Save table assignments to localStorage
   * @param {string} documentId - The document ID
   * @param {Array} assignments - Array of table assignment objects
   * @returns {boolean} Success status
   */
  save(documentId, assignments) {
    try {
      const key = this.getStorageKey(documentId)
      if (!key) return false

      const dataToStore = {
        documentId,
        assignments,
        savedAt: new Date().toISOString(),
        version: '1.0'
      }

      localStorage.setItem(key, JSON.stringify(dataToStore))
      console.log(`TableAssignmentStorage: Saved ${assignments.length} assignments for document ${documentId}`)
      return true
    } catch (error) {
      console.error('TableAssignmentStorage: Error saving assignments:', error)
      return false
    }
  }

  /**
   * Load table assignments from localStorage
   * @param {string} documentId - The document ID
   * @returns {Array|null} Array of assignments or null if not found
   */
  load(documentId) {
    try {
      const key = this.getStorageKey(documentId)
      if (!key) return null

      const storedData = localStorage.getItem(key)
      if (!storedData) {
        console.log(`TableAssignmentStorage: No saved assignments for document ${documentId}`)
        return null
      }

      const parsedData = JSON.parse(storedData)
      console.log(`TableAssignmentStorage: Loaded ${parsedData.assignments.length} assignments for document ${documentId}`)
      
      // Return the assignments array (even if empty)
      // Empty array means user explicitly removed all rows
      return parsedData.assignments || []
    } catch (error) {
      console.error('TableAssignmentStorage: Error loading assignments:', error)
      return null
    }
  }

  /**
   * Save template tables array to localStorage
   * @param {string} documentId - The document ID
   * @param {Array} tables - Array of template table objects
   * @returns {boolean} Success status
   */
  saveTemplateTables(documentId, tables) {
    try {
      if (!documentId) return false
      const key = `${this.templateTablesKeyPrefix}${documentId}`
      
      const dataToStore = {
        documentId,
        tables,
        savedAt: new Date().toISOString()
      }
      
      localStorage.setItem(key, JSON.stringify(dataToStore))
      console.log(`TableAssignmentStorage: Saved ${tables.length} template tables for document ${documentId}`)
      return true
    } catch (error) {
      console.error('TableAssignmentStorage: Error saving template tables:', error)
      return false
    }
  }

  /**
   * Load template tables array from localStorage
   * @param {string} documentId - The document ID
   * @returns {Array|null} Array of template tables or null if not found
   */
  loadTemplateTables(documentId) {
    try {
      if (!documentId) return null
      const key = `${this.templateTablesKeyPrefix}${documentId}`
      
      const storedData = localStorage.getItem(key)
      if (!storedData) return null
      
      const parsedData = JSON.parse(storedData)
      console.log(`TableAssignmentStorage: Loaded ${parsedData.tables.length} template tables for document ${documentId}`)
      return parsedData.tables || []
    } catch (error) {
      console.error('TableAssignmentStorage: Error loading template tables:', error)
      return null
    }
  }

  /**
   * Save replacement tables array to localStorage with append-only behavior
   * @param {string} documentId - The document ID
   * @param {Array} tables - Array of replacement table objects
   * @param {boolean} appendOnly - If true, merge with existing tables (never shrink)
   * @returns {boolean} Success status
   */
  saveReplacementTables(documentId, tables, appendOnly = true) {
    try {
      if (!documentId) return false
      const key = `${this.replacementTablesKeyPrefix}${documentId}`
      
      let tablesToStore = tables
      
      if (appendOnly) {
        // Load existing tables and merge with append-only behavior
        const existingData = this.loadReplacementTables(documentId)
        if (existingData && existingData.tables && existingData.tables.length > 0) {
          // Start with all existing tables
          const mergedTables = [...existingData.tables]
          
          // Add any new tables that don't exist in storage
          tables.forEach(table => {
            const existingIndex = mergedTables.findIndex(t => t.id === table.id)
            if (existingIndex === -1) {
              // New table not in storage - append it
              mergedTables.push(table)
            }
          })
          
          tablesToStore = mergedTables
          console.log(`TableAssignmentStorage: Append-only merge - had ${existingData.tables.length}, new ${tables.length}, storing ${tablesToStore.length}`)
        }
      }
      
      const dataToStore = {
        documentId,
        tables: tablesToStore,
        savedAt: new Date().toISOString()
      }
      
      localStorage.setItem(key, JSON.stringify(dataToStore))
      console.log(`TableAssignmentStorage: Saved ${tablesToStore.length} replacement tables for document ${documentId}`)
      return true
    } catch (error) {
      console.error('TableAssignmentStorage: Error saving replacement tables:', error)
      return false
    }
  }

  /**
   * Load replacement tables array from localStorage
   * @param {string} documentId - The document ID
   * @returns {Object|null} Object with tables array and savedAt timestamp, or null
   */
  loadReplacementTables(documentId) {
    try {
      if (!documentId) return null
      const key = `${this.replacementTablesKeyPrefix}${documentId}`
      
      const storedData = localStorage.getItem(key)
      if (!storedData) return null
      
      const parsedData = JSON.parse(storedData)
      console.log(`TableAssignmentStorage: Loaded ${parsedData.tables.length} replacement tables for document ${documentId}`)
      
      // Return both tables and savedAt for checking if refresh is needed
      return {
        tables: parsedData.tables || [],
        savedAt: parsedData.savedAt
      }
    } catch (error) {
      console.error('TableAssignmentStorage: Error loading replacement tables:', error)
      return null
    }
  }

  /**
   * Clear all stored data for a document (assignments and table arrays)
   * @param {string} documentId - The document ID
   * @returns {boolean} Success status
   */
  clear(documentId) {
    try {
      const assignmentsKey = this.getStorageKey(documentId)
      const templateKey = `${this.templateTablesKeyPrefix}${documentId}`
      const replacementKey = `${this.replacementTablesKeyPrefix}${documentId}`
      
      if (assignmentsKey) localStorage.removeItem(assignmentsKey)
      localStorage.removeItem(templateKey)
      localStorage.removeItem(replacementKey)
      
      console.log(`TableAssignmentStorage: Cleared all data for document ${documentId}`)
      return true
    } catch (error) {
      console.error('TableAssignmentStorage: Error clearing data:', error)
      return false
    }
  }

  /**
   * Check if assignments exist for a document
   * @param {string} documentId - The document ID
   * @returns {boolean} True if assignments exist
   */
  exists(documentId) {
    const key = this.getStorageKey(documentId)
    return key ? localStorage.getItem(key) !== null : false
  }

  /**
   * Get metadata about stored assignments
   * @param {string} documentId - The document ID
   * @returns {Object|null} Metadata object or null
   */
  getMetadata(documentId) {
    try {
      const key = this.getStorageKey(documentId)
      if (!key) return null

      const storedData = localStorage.getItem(key)
      if (!storedData) return null

      const parsedData = JSON.parse(storedData)
      return {
        documentId: parsedData.documentId,
        savedAt: parsedData.savedAt,
        assignmentCount: parsedData.assignments.length,
        version: parsedData.version
      }
    } catch (error) {
      console.error('TableAssignmentStorage: Error getting metadata:', error)
      return null
    }
  }

  /**
   * Clear all table assignments from localStorage
   * @returns {number} Number of cleared entries
   */
  clearAll() {
    let clearedCount = 0
    const keysToRemove = []

    // Find all keys with our prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(this.storageKeyPrefix)) {
        keysToRemove.push(key)
      }
    }

    // Remove found keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      clearedCount++
    })

    console.log(`TableAssignmentStorage: Cleared ${clearedCount} assignment entries`)
    return clearedCount
  }

  /**
   * Get all stored document IDs
   * @returns {Array} Array of document IDs
   */
  getAllDocumentIds() {
    const documentIds = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(this.storageKeyPrefix)) {
        const documentId = key.replace(this.storageKeyPrefix, '')
        documentIds.push(documentId)
      }
    }

    return documentIds
  }
}

// Export singleton instance
export const tableAssignmentStorage = new TableAssignmentStorage()